import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react';
import { useQnAContext } from '../../QnAWidget/contexts/QnAContext';
import { configurationService } from '../services';
import {
  AIServiceConfig,
  ConfigurationModalState,
  AIServiceConfigValidation,
  DEFAULT_AI_CONFIG,
} from '../services/types';

interface ConfigurationContextType extends ConfigurationModalState {
  openConfigModal: () => void;
  closeConfigModal: () => void;
  setActiveTab: (tab: 'backend-override' | 'advanced') => void;
  updateConfig: (updates: Partial<AIServiceConfig>) => Promise<boolean>;
  validateField: (field: keyof AIServiceConfig, value: unknown) => Promise<void>;
  saveConfiguration: () => Promise<boolean>;
  resetToDefaults: () => Promise<void>;
  exportConfiguration: () => Promise<void>;
  importConfiguration: (configJson: string) => Promise<boolean>;
  getCurrentConfig: () => Promise<AIServiceConfig>;
}

interface ConfigurationContextProviderProps {
  children: ReactNode;
}

const QnAConfigContext = createContext<ConfigurationContextType | undefined>(undefined);

const createEmptyValidation = (): AIServiceConfigValidation => ({
  maxContextMessages: { isValid: true },
  provider: { isValid: true },
  model: { isValid: true },
  maxTokens: { isValid: true },
  temperature: { isValid: true },
  documentId: { isValid: true },
});

export function QnAConfigContextProvider({ children }: ConfigurationContextProviderProps) {
  const { refreshAIConfiguration, showNotification } = useQnAContext();
  const [state, setState] = useState<ConfigurationModalState>({
    isOpen: false,
    activeTab: 'backend-override',
    config: { ...DEFAULT_AI_CONFIG },
    validation: createEmptyValidation(),
    isValidating: false,
    isSaving: false,
    hasUnsavedChanges: false,
  });

  const loadConfiguration = useCallback(async () => {
    try {
      const config = await configurationService.getConfig();
      setState((prev) => ({
        ...prev,
        config,
        hasUnsavedChanges: false,
      }));
    } catch (error) {
      // Silently handle configuration loading errors
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const openConfigModal = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const closeConfigModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      hasUnsavedChanges: false,
      validation: createEmptyValidation(),
    }));
    loadConfiguration();
  }, [loadConfiguration]);

  const setActiveTab = useCallback((tab: 'backend-override' | 'advanced') => {
    setState((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const updateConfig = useCallback(async (updates: Partial<AIServiceConfig>): Promise<boolean> => {
    setState((prev) => ({
      ...prev,
      config: { ...prev.config, ...updates },
      hasUnsavedChanges: true,
    }));
    return true;
  }, []);

  const validateField = useCallback(async (
    field: keyof AIServiceConfig,
    value: unknown,
  ): Promise<void> => {
    setState((prev) => ({ ...prev, isValidating: true }));

    try {
      let validationResult: { isValid: boolean; error?: string } = { isValid: true };

      switch (field) {
        case 'provider':
          if (value && typeof value === 'string') {
            validationResult = configurationService.validationService.validateProvider(value);
          }
          break;
        case 'model':
          if (value && typeof value === 'string') {
            validationResult = configurationService.validationService.validateModel(value);
          }
          break;
        case 'maxTokens':
          if (value !== undefined && value !== null) {
            validationResult = configurationService.validationService.validateMaxTokens(value as number);
          }
          break;
        case 'temperature':
          if (value !== undefined && value !== null) {
            validationResult = configurationService.validationService.validateTemperature(value as number);
          }
          break;
        case 'documentId':
          if (value && typeof value === 'string') {
            validationResult = configurationService.validationService.validateDocumentId(value);
          }
          break;
        default:
          break;
      }

      setState((prev) => ({
        ...prev,
        validation: {
          ...prev.validation,
          [field]: validationResult,
        },
        isValidating: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        validation: {
          ...prev.validation,
          [field]: {
            isValid: false,
            error: 'Validation error',
          },
        },
        isValidating: false,
      }));
    }
  }, []);

  const saveConfiguration = useCallback(async (): Promise<boolean> => {
    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      const success = await configurationService.setConfig(state.config);

      if (success) {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          hasUnsavedChanges: false,
        }));
        await refreshAIConfiguration();
        showNotification('Configuration saved successfully', 'success');
      } else {
        setState((prev) => ({ ...prev, isSaving: false }));
        showNotification('Failed to save configuration', 'warning');
      }

      return success;
    } catch (error) {
      setState((prev) => ({ ...prev, isSaving: false }));
      showNotification('Error saving configuration', 'warning');
      return false;
    }
  }, [state.config, refreshAIConfiguration, showNotification]);

  const resetToDefaults = useCallback(async (): Promise<void> => {
    await configurationService.resetToDefaults();
    setState((prev) => ({
      ...prev,
      config: { ...DEFAULT_AI_CONFIG },
      validation: createEmptyValidation(),
      hasUnsavedChanges: false,
    }));
  }, []);

  const exportConfiguration = useCallback(async (): Promise<void> => {
    const configJson = await configurationService.exportConfig();

    const blob = new Blob([configJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qna-widget-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const importConfiguration = useCallback(async (configJson: string): Promise<boolean> => {
    try {
      const parsedConfig = await configurationService.parseImportConfig(configJson);

      if (parsedConfig) {
        setState((prev) => ({
          ...prev,
          config: parsedConfig,
          hasUnsavedChanges: true,
        }));
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, []);

  const getCurrentConfig = useCallback(async (): Promise<AIServiceConfig> => await configurationService.getConfig(), []);

  const value: ConfigurationContextType = useMemo(() => ({
    ...state,
    openConfigModal,
    closeConfigModal,
    setActiveTab,
    updateConfig,
    validateField,
    saveConfiguration,
    resetToDefaults,
    exportConfiguration,
    importConfiguration,
    getCurrentConfig,
  }), [
    state,
    openConfigModal,
    closeConfigModal,
    setActiveTab,
    updateConfig,
    validateField,
    saveConfiguration,
    resetToDefaults,
    exportConfiguration,
    importConfiguration,
    getCurrentConfig,
  ]);

  return (
    <QnAConfigContext.Provider value={value}>
      {children}
    </QnAConfigContext.Provider>
  );
}

export const useQnAConfigContext = (): ConfigurationContextType => {
  const context = useContext(QnAConfigContext);
  if (context === undefined) {
    throw new Error('useConfigurationContext must be used within a ConfigurationContextProvider');
  }
  return context;
};
